import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { expensesApi } from '../services/api';
import './Expenses.css';

type BillingCycle = 'monthly' | 'annual';

interface ExpensePaymentState {
  is_paid: boolean;
  amount_paid: number | null;
  paid_date: string | null;
  notes: string | null;
}

interface ExpenseBase {
  id: number;
  name: string;
  vendor: string | null;
  category: string | null;
  expected_amount: number;
  billing_cycle: BillingCycle;
  due_month: number | null;
  due_day: number | null;
  start_period: string | null;
  payment_method: string | null;
  notes: string | null;
  is_active: boolean;
}

interface ExpenseRow extends ExpenseBase {
  payment: ExpensePaymentState | null;
}

interface GridExpenseRow extends ExpenseBase {
  tracked_from: string;
  payments: Record<string, ExpensePaymentState>;
}

interface MonthSummary {
  expected_total: number;
  paid_total: number;
  outstanding_total: number;
  paid_count: number;
  total_count: number;
  variance: number;
}

interface HistoryMonth {
  period: string;
  paid_total: number;
  paid_count: number;
}

interface ExpenseCategory {
  id: number;
  name: string;
  expense_count: number;
}

interface ExpenseForm {
  name: string;
  vendor: string;
  category: string;
  expected_amount: string;
  billing_cycle: BillingCycle;
  due_month: string;
  due_day: string;
  start_period: string;
  payment_method: string;
  notes: string;
}

const EMPTY_FORM: ExpenseForm = {
  name: '', vendor: '', category: '', expected_amount: '', billing_cycle: 'monthly',
  due_month: '', due_day: '', start_period: '', payment_method: '', notes: '',
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// paid/partial = a recorded payment; due = explicitly flagged as still owing;
// unpaid = owed (derived) but nothing recorded; na = annual expense, not its
// month; future / untracked = not (yet) owed.
type GridCellStatus = 'paid' | 'partial' | 'due' | 'unpaid' | 'na' | 'future' | 'untracked';

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const NEW_CATEGORY = '__new__';

const fmtR = (v: number | null | undefined) =>
  `R ${(v ?? 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Compact rand for grid cells — no decimals, no prefix, space thousands.
const fmtCell = (v: number) =>
  Math.round(v).toLocaleString('en-ZA', { maximumFractionDigits: 0 });

const pad2 = (n: number) => String(n).padStart(2, '0');

const todayPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
};

const shiftPeriod = (period: string, delta: number) => {
  const [y, m] = period.split('-').map(Number);
  const date = new Date(y, m - 1 + delta, 1);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
};

const periodLabel = (period: string) => {
  const [y, m] = period.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

const shortPeriodLabel = (period: string) => {
  const [y, m] = period.split('-').map(Number);
  return `${MONTH_NAMES[m - 1].slice(0, 3)} '${String(y).slice(2)}`;
};

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// "Vodacom · due 7th" / "Santam · annual · due Mar 1st"
const rowMeta = (row: ExpenseBase) => {
  const parts: string[] = [];
  if (row.vendor) parts.push(row.vendor);
  if (row.billing_cycle === 'annual') {
    const month = row.due_month ? MONTH_NAMES[row.due_month - 1].slice(0, 3) : '?';
    parts.push(`annual · due ${month}${row.due_day != null ? ` ${ordinal(row.due_day)}` : ''}`);
  } else if (row.due_day != null) {
    parts.push(`due ${ordinal(row.due_day)}`);
  }
  return parts.join(' · ');
};

const VIEW_STORAGE_KEY = 'cv-expenses-view';

interface GridPopover {
  id: number;
  month: number;
  top: number;
  left: number;
}

const POPOVER_WIDTH = 300;
const POPOVER_HEIGHT = 200;

function Expenses() {
  const [view, setView] = useState<'grid' | 'month'>(() =>
    localStorage.getItem(VIEW_STORAGE_KEY) === 'month' ? 'month' : 'grid');
  const [period, setPeriod] = useState(todayPeriod());
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [gridRows, setGridRows] = useState<GridExpenseRow[]>([]);
  const [summary, setSummary] = useState<MonthSummary | null>(null);
  const [history, setHistory] = useState<HistoryMonth[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());

  // inline edit of one payment cell at a time (month view)
  const [editCell, setEditCell] = useState<{ id: number; field: 'amount' | 'date' | 'note' } | null>(null);
  const [editValue, setEditValue] = useState('');

  // payment popover for one grid cell at a time (year view)
  const [gridPop, setGridPop] = useState<GridPopover | null>(null);
  const [gridPopValue, setGridPopValue] = useState('');

  // add/edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ExpenseForm>(EMPTY_FORM);
  const [newCategoryMode, setNewCategoryMode] = useState(false);
  const [modalSaving, setModalSaving] = useState(false);

  // category maintenance modal
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const [catEditId, setCatEditId] = useState<number | null>(null);
  const [catEditName, setCatEditName] = useState('');

  const isCurrentMonth = period === todayPeriod();
  const isPastMonth = period < todayPeriod();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  useEffect(() => { localStorage.setItem(VIEW_STORAGE_KEY, view); }, [view]);

  const load = useCallback(async (withSpinner = true) => {
    if (withSpinner) setLoading(true);
    try {
      const [month, hist] = await Promise.all([
        expensesApi.month(period, showArchived),
        expensesApi.history(12).catch(() => null),
      ]);
      setRows(month.expenses);
      setSummary(month.summary);
      if (hist) setHistory(hist.months);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to load expenses');
    } finally {
      if (withSpinner) setLoading(false);
    }
  }, [period, showArchived]);

  const loadGrid = useCallback(async (withSpinner = true) => {
    if (withSpinner) setLoading(true);
    try {
      const data = await expensesApi.grid(year, showArchived);
      setGridRows(data.expenses);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to load expenses');
    } finally {
      if (withSpinner) setLoading(false);
    }
  }, [year, showArchived]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await expensesApi.categories();
      setCategories(data.categories);
    } catch { /* picker falls back to what the rows already carry */ }
  }, []);

  useEffect(() => { if (view === 'month') load(); }, [view, load]);
  useEffect(() => { if (view === 'grid') loadGrid(); }, [view, loadGrid]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const reloadActive = useCallback(
    () => (view === 'grid' ? loadGrid(false) : load(false)),
    [view, loadGrid, load]);

  // Picker options: the maintained list, plus whatever the form currently
  // holds (an expense may still carry a name that was deleted from the list).
  const categoryOptions = useMemo(() => {
    const names = categories.map(c => c.name);
    if (form.category && !names.includes(form.category)) names.push(form.category);
    return names.sort((a, b) => a.localeCompare(b));
  }, [categories, form.category]);

  const grouped = useMemo(() => {
    const map = new Map<string, ExpenseRow[]>();
    rows.forEach(r => {
      const key = r.category || 'Uncategorised';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return Array.from(map.entries());
  }, [rows]);

  const gridGrouped = useMemo(() => {
    const map = new Map<string, GridExpenseRow[]>();
    gridRows.forEach(r => {
      const key = r.category || 'Uncategorised';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return Array.from(map.entries());
  }, [gridRows]);

  const markSaving = (id: number, on: boolean) => {
    setSavingIds(prev => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };

  const applyRowUpdate = (updated: ExpenseRow) => {
    setRows(prev => prev.map(r => (r.id === updated.id ? updated : r)));
  };

  const refreshSummary = async () => {
    try {
      const month = await expensesApi.month(period, showArchived);
      setRows(month.expenses);
      setSummary(month.summary);
      const hist = await expensesApi.history(12).catch(() => null);
      if (hist) setHistory(hist.months);
    } catch { /* keep current view */ }
  };

  const togglePaid = async (row: ExpenseRow) => {
    if (savingIds.has(row.id)) return;
    const paying = !(row.payment?.is_paid);
    markSaving(row.id, true);
    try {
      const updated = await expensesApi.setPayment(row.id, period, { is_paid: paying });
      applyRowUpdate(updated);
      await refreshSummary();
      toast.success(paying ? `${row.name} ticked off as paid` : `${row.name} marked unpaid`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    } finally {
      markSaving(row.id, false);
    }
  };

  const startCellEdit = (row: ExpenseRow, field: 'amount' | 'date' | 'note') => {
    setEditCell({ id: row.id, field });
    if (field === 'amount') setEditValue(row.payment?.amount_paid != null ? String(row.payment.amount_paid) : '');
    if (field === 'date') setEditValue(row.payment?.paid_date || '');
    if (field === 'note') setEditValue(row.payment?.notes || '');
  };

  const saveCellEdit = async (row: ExpenseRow) => {
    if (!editCell) return;
    const { field } = editCell;
    setEditCell(null);
    const data: Record<string, any> = {};
    if (field === 'amount') {
      const amount = parseFloat(editValue);
      if (editValue.trim() === '' || isNaN(amount) || amount < 0) {
        toast.error('Enter a valid amount');
        return;
      }
      data.amount_paid = amount;
    } else if (field === 'date') {
      data.paid_date = editValue || null;
    } else {
      data.notes = editValue;
    }
    markSaving(row.id, true);
    try {
      const updated = await expensesApi.setPayment(row.id, period, data);
      applyRowUpdate(updated);
      if (field === 'amount') await refreshSummary();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      markSaving(row.id, false);
    }
  };

  // ----- Year grid helpers -----

  const gridStatus = (row: GridExpenseRow, month: number): GridCellStatus => {
    const mm = pad2(month);
    const cellPeriod = `${year}-${mm}`;
    const payment = row.payments[mm];
    if (payment?.is_paid) {
      const paid = payment.amount_paid ?? row.expected_amount;
      return paid + 0.005 >= row.expected_amount ? 'paid' : 'partial';
    }
    if (payment) return 'due';
    if (row.billing_cycle === 'annual' && row.due_month !== month) return 'na';
    if (cellPeriod > todayPeriod()) return 'future';
    if (cellPeriod < row.tracked_from) return 'untracked';
    return 'unpaid';
  };

  const gridCellTitle = (row: GridExpenseRow, month: number, status: GridCellStatus): string => {
    const mm = pad2(month);
    const payment = row.payments[mm];
    const lines = [
      `${row.name} — ${MONTH_NAMES[month - 1]} ${year}`,
      `Expected ${fmtR(row.expected_amount)}${row.billing_cycle === 'annual' ? ' per year' : ''}`,
    ];
    if (payment?.is_paid) {
      lines.push(`Paid ${fmtR(payment.amount_paid ?? row.expected_amount)}${payment.paid_date ? ` on ${payment.paid_date}` : ''}`);
    } else if (status === 'due') {
      lines.push('Flagged as still due');
    } else if (status === 'unpaid') {
      lines.push('Not paid');
    } else if (status === 'na') {
      lines.push('Annual expense — not owed this month');
    } else if (status === 'future') {
      lines.push('Not due yet');
    } else {
      lines.push(`Before this expense started (${periodLabel(row.tracked_from)})`);
    }
    if (payment?.notes) lines.push(payment.notes);
    lines.push('Click to record a payment, flag it as still due, or clear it');
    return lines.join('\n');
  };

  const openGridPop = (row: GridExpenseRow, month: number, e: React.MouseEvent<HTMLElement>) => {
    if (savingIds.has(row.id)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 8));
    const below = rect.bottom + 6;
    const top = below + POPOVER_HEIGHT > window.innerHeight ? Math.max(8, rect.top - POPOVER_HEIGHT - 6) : below;
    const payment = row.payments[pad2(month)];
    setGridPopValue(payment?.is_paid && payment.amount_paid != null
      ? String(payment.amount_paid)
      : String(row.expected_amount));
    setGridPop({ id: row.id, month, top, left });
  };

  const closeGridPop = () => setGridPop(null);

  const applyGridPayment = (expenseId: number, mm: string, payment: ExpensePaymentState | null) => {
    setGridRows(prev => prev.map(r => {
      if (r.id !== expenseId) return r;
      const payments = { ...r.payments };
      if (payment) payments[mm] = payment; else delete payments[mm];
      return { ...r, payments };
    }));
  };

  const popRow = gridPop ? gridRows.find(r => r.id === gridPop.id) || null : null;
  const popPeriod = gridPop ? `${year}-${pad2(gridPop.month)}` : '';
  const popPayment = popRow && gridPop ? popRow.payments[pad2(gridPop.month)] : undefined;

  const popSavePaid = async () => {
    if (!gridPop || !popRow) return;
    const amount = parseFloat(gridPopValue.trim().replace(',', '.'));
    if (isNaN(amount) || amount < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const { id, month } = gridPop;
    closeGridPop();
    markSaving(id, true);
    try {
      const updated = await expensesApi.setPayment(id, popPeriod, { is_paid: true, amount_paid: amount });
      applyGridPayment(id, pad2(month), updated.payment);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to save payment');
    } finally {
      markSaving(id, false);
    }
  };

  const popMarkDue = async () => {
    if (!gridPop || !popRow) return;
    const { id, month } = gridPop;
    const name = popRow.name;
    closeGridPop();
    markSaving(id, true);
    try {
      const updated = await expensesApi.setPayment(id, popPeriod, { is_paid: false });
      applyGridPayment(id, pad2(month), updated.payment);
      toast.success(`${name} — ${MONTH_NAMES[month - 1]} flagged as still due`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    } finally {
      markSaving(id, false);
    }
  };

  const popClear = async () => {
    if (!gridPop || !popRow) return;
    const { id, month } = gridPop;
    const name = popRow.name;
    closeGridPop();
    markSaving(id, true);
    try {
      await expensesApi.clearPayment(id, popPeriod);
      applyGridPayment(id, pad2(month), null);
      toast.success(`${name} — ${MONTH_NAMES[month - 1]} cleared`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to clear payment');
    } finally {
      markSaving(id, false);
    }
  };

  const monthPaidTotals = useMemo(() => {
    const totals = Array(12).fill(0) as number[];
    gridRows.forEach(row => {
      for (let m = 1; m <= 12; m++) {
        const payment = row.payments[pad2(m)];
        if (payment?.is_paid) totals[m - 1] += payment.amount_paid ?? row.expected_amount;
      }
    });
    return totals;
  }, [gridRows]);

  const gridGrandTotal = monthPaidTotals.reduce((sum, v) => sum + v, 0);

  const goToMonth = (month: number) => {
    setPeriod(`${year}-${pad2(month)}`);
    setView('month');
  };

  // ----- Add / edit modal -----

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setNewCategoryMode(false);
    setModalOpen(true);
  };

  const openEdit = (row: ExpenseBase) => {
    setEditingId(row.id);
    setForm({
      name: row.name,
      vendor: row.vendor || '',
      category: row.category || '',
      expected_amount: String(row.expected_amount),
      billing_cycle: row.billing_cycle || 'monthly',
      due_month: row.due_month != null ? String(row.due_month) : '',
      due_day: row.due_day != null ? String(row.due_day) : '',
      start_period: row.start_period || '',
      payment_method: row.payment_method || '',
      notes: row.notes || '',
    });
    setNewCategoryMode(false);
    setModalOpen(true);
  };

  const setCycle = (cycle: BillingCycle) => {
    setForm(f => ({
      ...f,
      billing_cycle: cycle,
      due_month: cycle === 'annual' ? (f.due_month || String(currentMonth)) : '',
    }));
  };

  const onCategoryPick = (value: string) => {
    if (value === NEW_CATEGORY) {
      setNewCategoryMode(true);
      setForm(f => ({ ...f, category: '' }));
    } else {
      setForm(f => ({ ...f, category: value }));
    }
  };

  const submitModal = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(form.expected_amount);
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (isNaN(amount) || amount < 0) { toast.error('Enter a valid expected amount'); return; }
    const dueDay = form.due_day.trim() === '' ? null : parseInt(form.due_day, 10);
    if (dueDay != null && (isNaN(dueDay) || dueDay < 1 || dueDay > 31)) {
      toast.error('Due day must be between 1 and 31');
      return;
    }
    const dueMonth = form.billing_cycle === 'annual' ? parseInt(form.due_month, 10) : null;
    if (form.billing_cycle === 'annual' && (dueMonth == null || isNaN(dueMonth) || dueMonth < 1 || dueMonth > 12)) {
      toast.error('Pick the month an annual expense falls due');
      return;
    }
    const startPeriod = form.start_period.trim();
    if (startPeriod && !PERIOD_RE.test(startPeriod)) {
      toast.error('First month must look like 2026-03');
      return;
    }
    const category = form.category.trim();
    const data = {
      name: form.name.trim(),
      vendor: form.vendor.trim() || null,
      category: category || null,
      expected_amount: amount,
      billing_cycle: form.billing_cycle,
      due_month: dueMonth,
      due_day: dueDay,
      start_period: startPeriod || null,
      payment_method: form.payment_method.trim() || null,
      notes: form.notes.trim() || null,
    };
    setModalSaving(true);
    try {
      if (newCategoryMode && category && !categories.some(c => c.name.toLowerCase() === category.toLowerCase())) {
        try {
          await expensesApi.createCategory(category);
        } catch (err: any) {
          if (err.response?.status !== 409) throw err;
        }
      }
      if (editingId != null) {
        await expensesApi.update(editingId, data);
        toast.success('Expense updated');
      } else {
        await expensesApi.create(data);
        toast.success('Expense added');
      }
      setModalOpen(false);
      await Promise.all([reloadActive(), loadCategories()]);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    } finally {
      setModalSaving(false);
    }
  };

  const archiveExpense = async (row: ExpenseBase) => {
    if (!window.confirm(`Archive "${row.name}"? It keeps its payment history and disappears from new months.`)) return;
    try {
      await expensesApi.remove(row.id);
      toast.success(`${row.name} archived`);
      setModalOpen(false);
      await reloadActive();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to archive');
    }
  };

  const unarchiveExpense = async (row: ExpenseBase) => {
    try {
      await expensesApi.update(row.id, { is_active: true });
      toast.success(`${row.name} restored`);
      await reloadActive();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const deleteExpense = async (row: ExpenseBase) => {
    if (!window.confirm(`Permanently delete "${row.name}" AND its entire payment history? This cannot be undone.`)) return;
    try {
      await expensesApi.remove(row.id, true);
      toast.success(`${row.name} deleted`);
      setModalOpen(false);
      await Promise.all([reloadActive(), loadCategories()]);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // ----- Category maintenance -----

  const openCategories = () => {
    setNewCatName('');
    setCatEditId(null);
    setCatModalOpen(true);
    loadCategories();
  };

  const addCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newCatName.trim();
    if (!name) return;
    setCatSaving(true);
    try {
      await expensesApi.createCategory(name);
      setNewCatName('');
      toast.success(`Category "${name}" added`);
      await loadCategories();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to add category');
    } finally {
      setCatSaving(false);
    }
  };

  const startRename = (cat: ExpenseCategory) => {
    setCatEditId(cat.id);
    setCatEditName(cat.name);
  };

  const saveRename = async () => {
    if (catEditId == null) return;
    const cat = categories.find(c => c.id === catEditId);
    const name = catEditName.trim();
    setCatEditId(null);
    if (!cat || !name || name === cat.name) return;
    setCatSaving(true);
    try {
      const result = await expensesApi.renameCategory(cat.id, name);
      toast.success(
        result.expense_count > 0
          ? `Renamed to "${name}" — ${result.expense_count} expense${result.expense_count === 1 ? '' : 's'} updated`
          : `Renamed to "${name}"`);
      // the form may hold the old name (Manage opened from the expense modal)
      setForm(f => (f.category === cat.name ? { ...f, category: name } : f));
      await Promise.all([loadCategories(), reloadActive()]);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to rename category');
    } finally {
      setCatSaving(false);
    }
  };

  const removeCategory = async (cat: ExpenseCategory) => {
    const inUse = cat.expense_count > 0
      ? ` ${cat.expense_count} expense${cat.expense_count === 1 ? '' : 's'} will become Uncategorised (payment history is kept).`
      : '';
    if (!window.confirm(`Delete category "${cat.name}"?${inUse}`)) return;
    setCatSaving(true);
    try {
      await expensesApi.deleteCategory(cat.id);
      toast.success(`Category "${cat.name}" deleted`);
      setForm(f => (f.category === cat.name ? { ...f, category: '' } : f));
      await Promise.all([loadCategories(), reloadActive()]);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    } finally {
      setCatSaving(false);
    }
  };

  const isOverdue = (row: ExpenseRow) => {
    if (row.payment?.is_paid || !row.is_active) return false;
    if (isPastMonth) return true;
    if (!isCurrentMonth || row.due_day == null) return false;
    return new Date().getDate() > row.due_day;
  };

  const maxHistory = Math.max(1, ...history.map(h => h.paid_total));
  const paidPct = summary && summary.total_count > 0
    ? Math.round((summary.paid_count / summary.total_count) * 100) : 0;

  const renderPaymentCells = (row: ExpenseRow) => {
    const paid = row.payment?.is_paid;
    const amountEditing = editCell?.id === row.id && editCell.field === 'amount';
    const dateEditing = editCell?.id === row.id && editCell.field === 'date';
    const noteEditing = editCell?.id === row.id && editCell.field === 'note';
    const variance = paid && row.payment?.amount_paid != null
      ? row.payment.amount_paid - row.expected_amount : 0;

    return (
      <>
        <td className="num">
          {amountEditing ? (
            <input
              className="exp-cell-input"
              autoFocus
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') saveCellEdit(row);
                if (e.key === 'Escape') setEditCell(null);
              }}
              onBlur={() => setEditCell(null)}
            />
          ) : paid ? (
            <span
              className="exp-editable"
              title="Click to edit the amount paid"
              onClick={() => startCellEdit(row, 'amount')}
            >
              {fmtR(row.payment?.amount_paid)}
              {variance !== 0 && (
                <span className={`exp-variance ${variance > 0 ? 'over' : 'under'}`}>
                  {variance > 0 ? '▲' : '▼'} {fmtR(Math.abs(variance))}
                </span>
              )}
            </span>
          ) : (
            <span className="exp-muted">—</span>
          )}
        </td>
        <td>
          {dateEditing ? (
            <input
              className="exp-cell-input"
              type="date"
              autoFocus
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') saveCellEdit(row);
                if (e.key === 'Escape') setEditCell(null);
              }}
              onBlur={() => setEditCell(null)}
            />
          ) : paid ? (
            <span
              className="exp-editable"
              title="Click to edit the payment date"
              onClick={() => startCellEdit(row, 'date')}
            >
              {row.payment?.paid_date || '—'}
            </span>
          ) : (
            <span className="exp-muted">—</span>
          )}
        </td>
        <td className="exp-note-cell">
          {noteEditing ? (
            <input
              className="exp-cell-input wide"
              autoFocus
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') saveCellEdit(row);
                if (e.key === 'Escape') setEditCell(null);
              }}
              onBlur={() => setEditCell(null)}
              placeholder="e.g. paid via EFT, ref 1234"
            />
          ) : (
            <span
              className={`exp-editable ${row.payment?.notes ? '' : 'exp-note-empty'}`}
              title="Click to add a note for this month's payment"
              onClick={() => startCellEdit(row, 'note')}
            >
              {row.payment?.notes || '＋ note'}
            </span>
          )}
        </td>
      </>
    );
  };

  const renderGridCell = (row: GridExpenseRow, month: number) => {
    const mm = pad2(month);
    const payment = row.payments[mm];
    const status = gridStatus(row, month);
    const active = gridPop?.id === row.id && gridPop.month === month;

    let label = '';
    if (status === 'paid' || status === 'partial') {
      label = fmtCell(payment!.amount_paid ?? row.expected_amount);
    } else if (status === 'unpaid') {
      label = '—';
    } else if (status === 'due') {
      label = 'due';
    }

    return (
      <td key={mm} className="exp-gcell-td">
        <span
          className={`exp-gcell ${status} ${active ? 'active' : ''}`}
          title={gridCellTitle(row, month, status)}
          onClick={e => openGridPop(row, month, e)}
        >
          {label}
        </span>
      </td>
    );
  };

  const renderRowActions = (row: ExpenseBase) => (
    <>
      <button className="exp-mini" title="Edit expense" onClick={() => openEdit(row)}>✎</button>
      {row.is_active ? (
        <button className="exp-mini" title="Archive expense" onClick={() => archiveExpense(row)}>🗄</button>
      ) : (
        <button className="exp-mini" title="Restore expense" onClick={() => unarchiveExpense(row)}>↩</button>
      )}
    </>
  );

  return (
    <div className="expenses-page">
      <div className="page-header">
        <div>
          <h1>Expenses</h1>
          <p className="page-subtitle">
            Recurring business expenses, monthly or annual — add each one once, then capture what was paid per month
          </p>
        </div>
        <div className="exp-header-actions">
          <button className="exp-btn" onClick={openCategories}>Categories</button>
          <button className="exp-btn primary" onClick={openAdd}>＋ Add expense</button>
        </div>
      </div>

      {/* Navigation bar: year or month depending on view */}
      <div className="exp-monthbar">
        {view === 'grid' ? (
          <>
            <button className="exp-btn" onClick={() => setYear(y => y - 1)} aria-label="Previous year">‹</button>
            <span className="exp-month-label">{year}</span>
            <button className="exp-btn" onClick={() => setYear(y => y + 1)} aria-label="Next year">›</button>
            {year !== currentYear && (
              <button className="exp-btn sm" onClick={() => setYear(currentYear)}>Current year</button>
            )}
          </>
        ) : (
          <>
            <button className="exp-btn" onClick={() => setPeriod(p => shiftPeriod(p, -1))} aria-label="Previous month">‹</button>
            <span className="exp-month-label">{periodLabel(period)}</span>
            <button className="exp-btn" onClick={() => setPeriod(p => shiftPeriod(p, 1))} aria-label="Next month">›</button>
            {!isCurrentMonth && (
              <button className="exp-btn sm" onClick={() => setPeriod(todayPeriod())}>Current month</button>
            )}
          </>
        )}
        <div className="exp-viewtoggle" role="tablist">
          <button
            className={view === 'grid' ? 'active' : ''}
            onClick={() => setView('grid')}
          >Year grid</button>
          <button
            className={view === 'month' ? 'active' : ''}
            onClick={() => setView('month')}
          >Month detail</button>
        </div>
        <label className="exp-archived-toggle">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={e => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>

      {view === 'grid' ? (
        <>
          <div className="exp-legend">
            <span><i className="exp-dot paid" /> Paid</span>
            <span><i className="exp-dot partial" /> Partially paid</span>
            <span><i className="exp-dot unpaid" /> Not paid / still due</span>
            <span><i className="exp-dot future" /> Not owed (future, before start, or annual off-month)</span>
            <span className="exp-legend-hint">Click a cell to record a payment, flag it as still due, or clear it</span>
          </div>

          {loading ? (
            <div className="exp-empty">Loading…</div>
          ) : gridRows.length === 0 ? (
            <div className="exp-panel exp-empty">
              No recurring expenses yet. Click <strong>＋ Add expense</strong> to add the first one —
              rent, connectivity, insurance, software subscriptions, and so on.
            </div>
          ) : (
            <div className="exp-panel exp-grid-panel">
              <div className="exp-grid-wrap">
                <table className="exp-table exp-grid-table">
                  <thead>
                    <tr>
                      <th className="exp-grid-name-col">Expense</th>
                      <th className="num">Expected</th>
                      {MONTH_NAMES.map((name, i) => (
                        <th
                          key={name}
                          className={`num exp-grid-month ${year === currentYear && i + 1 === currentMonth ? 'current' : ''}`}
                          title={`Open ${name} ${year} in month detail`}
                          onClick={() => goToMonth(i + 1)}
                        >
                          {name.slice(0, 3)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {gridGrouped.map(([category, catRows]) => (
                      <React.Fragment key={category}>
                        <tr className="exp-cat-row">
                          <td colSpan={14}>{category}</td>
                        </tr>
                        {catRows.map(row => (
                          <tr key={row.id} className={!row.is_active ? 'archived' : ''}>
                            <td className="exp-grid-name-col">
                              <div className="exp-name">
                                {row.name}
                                {row.billing_cycle === 'annual' && <span className="exp-chip cycle-chip">annual</span>}
                                {!row.is_active && <span className="exp-chip archived-chip">archived</span>}
                                <span className="exp-row-actions">{renderRowActions(row)}</span>
                              </div>
                              {rowMeta(row) && <div className="exp-vendor">{rowMeta(row)}</div>}
                            </td>
                            <td className="num exp-grid-expected">
                              {fmtCell(row.expected_amount)}
                              {row.billing_cycle === 'annual' && <span className="exp-cycle-suffix">/yr</span>}
                            </td>
                            {MONTH_NAMES.map((_, i) => renderGridCell(row, i + 1))}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                    <tr className="exp-total-row">
                      <td className="exp-grid-name-col">
                        <div>Paid per month</div>
                        <div className="exp-vendor">{year} total {gridGrandTotal > 0 ? fmtR(gridGrandTotal) : '—'}</div>
                      </td>
                      <td />
                      {monthPaidTotals.map((total, i) => (
                        <td key={i} className="num">{total > 0 ? fmtCell(total) : ''}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="exp-grid-footnote">Amounts in rand, rounded — hover a cell for exact figures.</div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Summary cards */}
          {summary && (
            <div className="exp-cards">
              <div className="exp-card">
                <div className="exp-card-label">Expected this month</div>
                <div className="exp-card-value">{fmtR(summary.expected_total)}</div>
                <div className="exp-card-sub">{summary.total_count} expense{summary.total_count === 1 ? '' : 's'} due</div>
              </div>
              <div className="exp-card">
                <div className="exp-card-label">Paid</div>
                <div className="exp-card-value paid">{fmtR(summary.paid_total)}</div>
                <div className="exp-card-sub">{summary.paid_count} of {summary.total_count} ticked off</div>
                <div className="exp-progress"><div className="exp-progress-fill" style={{ width: `${paidPct}%` }} /></div>
              </div>
              <div className="exp-card">
                <div className="exp-card-label">Outstanding</div>
                <div className={`exp-card-value ${summary.outstanding_total > 0 ? 'due' : 'paid'}`}>
                  {fmtR(summary.outstanding_total)}
                </div>
                <div className="exp-card-sub">
                  {summary.outstanding_total > 0
                    ? `${summary.total_count - summary.paid_count} still to pay`
                    : 'All paid up 🎉'}
                </div>
              </div>
              <div className="exp-card">
                <div className="exp-card-label">Paid per month — last 12 months</div>
                <div className="exp-trend">
                  {history.map(h => (
                    <div
                      key={h.period}
                      className={`exp-trend-bar ${h.period === period ? 'current' : ''}`}
                      title={`${shortPeriodLabel(h.period)}: ${fmtR(h.paid_total)} (${h.paid_count} paid)`}
                      onClick={() => setPeriod(h.period)}
                    >
                      <div className="exp-trend-fill" style={{ height: `${Math.max(4, Math.round((h.paid_total / maxHistory) * 100))}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Expense table */}
          {loading ? (
            <div className="exp-empty">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="exp-panel exp-empty">
              Nothing is due in {periodLabel(period)}. Click <strong>＋ Add expense</strong> to add one —
              rent, connectivity, insurance, software subscriptions, and so on.
            </div>
          ) : (
            <div className="exp-panel">
              <table className="exp-table">
                <thead>
                  <tr>
                    <th className="exp-check-col">Paid</th>
                    <th>Expense</th>
                    <th>Due</th>
                    <th className="num">Expected</th>
                    <th className="num">Amount paid</th>
                    <th>Paid on</th>
                    <th>Note</th>
                    <th className="exp-actions-col" />
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(([category, catRows]) => {
                    const catExpected = catRows.reduce((sum, r) => sum + r.expected_amount, 0);
                    const catPaid = catRows.reduce((sum, r) => sum + (r.payment?.is_paid ? (r.payment.amount_paid ?? r.expected_amount) : 0), 0);
                    return (
                      <React.Fragment key={category}>
                        <tr className="exp-cat-row">
                          <td colSpan={3}>{category}</td>
                          <td className="num">{fmtR(catExpected)}</td>
                          <td className="num">{catPaid > 0 ? fmtR(catPaid) : ''}</td>
                          <td colSpan={3} />
                        </tr>
                        {catRows.map(row => (
                          <tr
                            key={row.id}
                            className={[
                              row.payment?.is_paid ? 'paid' : '',
                              isOverdue(row) ? 'overdue' : '',
                              !row.is_active ? 'archived' : '',
                            ].join(' ')}
                          >
                            <td className="exp-check-col">
                              <input
                                type="checkbox"
                                className="exp-check"
                                checked={!!row.payment?.is_paid}
                                disabled={savingIds.has(row.id)}
                                onChange={() => togglePaid(row)}
                                title={row.payment?.is_paid ? 'Mark as unpaid' : 'Tick off as paid'}
                              />
                            </td>
                            <td>
                              <div className="exp-name">
                                {row.name}
                                {row.billing_cycle === 'annual' && <span className="exp-chip cycle-chip">annual</span>}
                                {!row.is_active && <span className="exp-chip archived-chip">archived</span>}
                                {isOverdue(row) && <span className="exp-chip overdue-chip">overdue</span>}
                              </div>
                              {(row.vendor || row.payment_method) && (
                                <div className="exp-vendor">
                                  {row.vendor}
                                  {row.vendor && row.payment_method ? ' · ' : ''}
                                  {row.payment_method}
                                </div>
                              )}
                            </td>
                            <td className="exp-due">{row.due_day != null ? ordinal(row.due_day) : '—'}</td>
                            <td className="num">
                              {fmtR(row.expected_amount)}
                              {row.billing_cycle === 'annual' && <span className="exp-cycle-suffix">/yr</span>}
                            </td>
                            {renderPaymentCells(row)}
                            <td className="exp-actions-col">{renderRowActions(row)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {summary && (
                    <tr className="exp-total-row">
                      <td colSpan={3}>Total</td>
                      <td className="num">{fmtR(summary.expected_total)}</td>
                      <td className="num">{fmtR(summary.paid_total)}</td>
                      <td colSpan={3} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Grid cell payment popover */}
      {gridPop && popRow && (
        <div className="exp-pop-backdrop" onClick={closeGridPop}>
          <div
            className="exp-pop"
            style={{ top: gridPop.top, left: gridPop.left, width: POPOVER_WIDTH }}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { if (e.key === 'Escape') closeGridPop(); }}
          >
            <div className="exp-pop-title">{popRow.name}</div>
            <div className="exp-pop-sub">
              {MONTH_NAMES[gridPop.month - 1]} {year} · expected {fmtR(popRow.expected_amount)}
              {popRow.billing_cycle === 'annual' ? ' per year' : ''}
            </div>
            {popPayment?.is_paid && (
              <div className="exp-pop-state paid">
                Paid {fmtR(popPayment.amount_paid ?? popRow.expected_amount)}
                {popPayment.paid_date ? ` on ${popPayment.paid_date}` : ''}
              </div>
            )}
            {popPayment && !popPayment.is_paid && (
              <div className="exp-pop-state due">Flagged as still due</div>
            )}
            <label className="exp-pop-field">
              <span>Amount paid (R)</span>
              <input
                autoFocus
                onFocus={e => e.target.select()}
                value={gridPopValue}
                onChange={e => setGridPopValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); popSavePaid(); } }}
                inputMode="decimal"
              />
            </label>
            <div className="exp-pop-actions">
              <button type="button" className="exp-btn primary sm" onClick={popSavePaid}>✓ Paid</button>
              <button type="button" className="exp-btn warn sm" onClick={popMarkDue}>Still due</button>
              {popPayment && (
                <button type="button" className="exp-btn sm" onClick={popClear} title="Remove this month's record">Clear</button>
              )}
              <span className="exp-pop-hint">Enter = paid · Esc = close</span>
            </div>
          </div>
        </div>
      )}

      {/* Add / edit modal */}
      {modalOpen && (
        <div className="exp-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="exp-modal" onClick={e => e.stopPropagation()}>
            <h2>{editingId != null ? 'Edit expense' : 'Add expense'}</h2>
            <form onSubmit={submitModal}>
              <label>
                Name *
                <input
                  autoFocus
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Office internet"
                />
              </label>
              <div className="exp-form-row">
                <label>
                  Vendor
                  <input
                    value={form.vendor}
                    onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                    placeholder="e.g. Vodacom"
                  />
                </label>
                <label>
                  <span className="exp-label-row">
                    Category
                    <button type="button" className="exp-link" onClick={openCategories}>Manage</button>
                  </span>
                  {newCategoryMode ? (
                    <div className="exp-inline-new">
                      <input
                        autoFocus
                        value={form.category}
                        onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                        placeholder="New category name"
                      />
                      <button
                        type="button"
                        className="exp-btn sm"
                        title="Back to the list"
                        onClick={() => { setNewCategoryMode(false); setForm(f => ({ ...f, category: '' })); }}
                      >✕</button>
                    </div>
                  ) : (
                    <select value={form.category} onChange={e => onCategoryPick(e.target.value)}>
                      <option value="">Uncategorised</option>
                      {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                      <option value={NEW_CATEGORY}>＋ New category…</option>
                    </select>
                  )}
                </label>
              </div>
              <div className="exp-form-row">
                <label>
                  Billing cycle
                  <select value={form.billing_cycle} onChange={e => setCycle(e.target.value as BillingCycle)}>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </label>
                {form.billing_cycle === 'annual' && (
                  <label>
                    Month due *
                    <select value={form.due_month} onChange={e => setForm(f => ({ ...f, due_month: e.target.value }))}>
                      {MONTH_NAMES.map((name, i) => <option key={name} value={String(i + 1)}>{name}</option>)}
                    </select>
                  </label>
                )}
                <label>
                  Expected amount (R) {form.billing_cycle === 'annual' ? 'per year' : 'per month'} *
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.expected_amount}
                    onChange={e => setForm(f => ({ ...f, expected_amount: e.target.value }))}
                    placeholder="0.00"
                  />
                </label>
              </div>
              <div className="exp-form-row">
                <label>
                  Due day of month
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={form.due_day}
                    onChange={e => setForm(f => ({ ...f, due_day: e.target.value }))}
                    placeholder="e.g. 7"
                  />
                </label>
                <label>
                  First month owed
                  <input
                    type="month"
                    value={form.start_period}
                    onChange={e => setForm(f => ({ ...f, start_period: e.target.value }))}
                    placeholder="YYYY-MM"
                    title="Months before this are not owed. Leave blank to start from when the expense was added."
                  />
                </label>
                <label>
                  Payment method
                  <input
                    value={form.payment_method}
                    onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}
                    placeholder="e.g. Debit order"
                  />
                </label>
              </div>
              <div className="exp-form-hint">
                First month owed: earlier months show as not tracked; leave blank to start from when the expense was added.
                Historical invoices can also be flagged one by one — click a grid cell and choose <strong>Still due</strong>.
              </div>
              <label>
                Notes
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Anything worth remembering about this expense"
                />
              </label>
              <div className="exp-modal-actions">
                {editingId != null && (
                  <button
                    type="button"
                    className="exp-btn danger"
                    onClick={() => {
                      const row = rows.find(r => r.id === editingId) || gridRows.find(r => r.id === editingId);
                      if (row) deleteExpense(row);
                    }}
                  >
                    Delete permanently
                  </button>
                )}
                <div className="exp-modal-actions-right">
                  <button type="button" className="exp-btn" onClick={() => setModalOpen(false)}>Cancel</button>
                  <button type="submit" className="exp-btn primary" disabled={modalSaving}>
                    {modalSaving ? 'Saving…' : editingId != null ? 'Save changes' : 'Add expense'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category maintenance modal (sits above the expense modal when opened from it) */}
      {catModalOpen && (
        <div className="exp-modal-overlay exp-cat-overlay" onClick={() => setCatModalOpen(false)}>
          <div className="exp-modal exp-cat-modal" onClick={e => e.stopPropagation()}>
            <h2>Expense categories</h2>
            <form className="exp-cat-add" onSubmit={addCategory}>
              <input
                autoFocus
                value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                placeholder="New category, e.g. Connectivity"
                maxLength={100}
              />
              <button type="submit" className="exp-btn primary" disabled={!newCatName.trim() || catSaving}>Add</button>
            </form>
            {categories.length === 0 ? (
              <div className="exp-empty">No categories yet — add the first one above.</div>
            ) : (
              <table className="exp-table exp-cat-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="num">Expenses</th>
                    <th className="exp-actions-col" />
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => (
                    <tr key={cat.id}>
                      <td>
                        {catEditId === cat.id ? (
                          <input
                            className="exp-cell-input wide"
                            autoFocus
                            value={catEditName}
                            onChange={e => setCatEditName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); saveRename(); }
                              if (e.key === 'Escape') setCatEditId(null);
                            }}
                            onBlur={() => setCatEditId(null)}
                            maxLength={100}
                          />
                        ) : (
                          <span className="exp-editable" title="Click to rename" onClick={() => startRename(cat)}>
                            {cat.name}
                          </span>
                        )}
                      </td>
                      <td className="num">{cat.expense_count}</td>
                      <td className="exp-actions-col">
                        <button className="exp-mini" title="Rename" onClick={() => startRename(cat)} disabled={catSaving}>✎</button>
                        <button className="exp-mini" title="Delete category" onClick={() => removeCategory(cat)} disabled={catSaving}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="exp-cat-hint">
              Renaming moves every expense in the category along. Deleting one leaves its expenses uncategorised — payment history is never touched.
            </div>
            <div className="exp-modal-actions">
              <div className="exp-modal-actions-right">
                <button type="button" className="exp-btn" onClick={() => setCatModalOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;
